import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LiquidacionService } from 'src/app/services/liquidacion/liquidacion.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { etiquetaCorte, etiquetaCortes } from 'src/app/utils/liquidacion-cortes';

/**
 * LIQUIDACIÓN · POR RESOLVER (5.3b/5.5; NOP-01 a 04). Lo que el banco no pagó vuelve al tesorero, no a un lote.
 *   · Propuesta nueva: una tanda de reintento en el cierre de hoy, que sigue las bandas por su monto (NOP-02).
 *   · Con la cuenta destino sin verificar solo se retiene (NOP-03); lo retenido vuelve mañana (NOP-04).
 *   · LOT-16: lo que el banco pagó después de anular su lote va aparte: se registra el pago o se descarta (con motivo).
 */
@Component({
  selector: 'app-liquidacion-por-resolver',
  templateUrl: './liquidacion-por-resolver.component.html'
})
export class LiquidacionPorResolverComponent implements OnInit {
  /** 13.4: el corte de cada tanda, liquidación o pago, tal como lo manda el núcleo. */
  readonly etiquetaCorte = etiquetaCorte;
  readonly etiquetaCortes = etiquetaCortes;
  @Input() dia = '';

  cargando = false;
  error = '';
  aviso = '';
  pagos: any[] = [];
  marcadas = new Set<string>();
  ocupado = false;
  reteniendo = false;
  motivoRetener = '';
  descartando: string | null = null;
  motivoDescartar = '';

  constructor(private liq: LiquidacionService, private router: Router, public _auth: AuthService) { }

  get puedePreparar(): boolean { return this._auth.hasPermission('liquidacion.preparar'); }

  // RSM-11 (Mario, 12-oct): todos ven los pagos no procesados; actuar (registrar, descartar, proponer, retener) sigue siendo del tesorero.
  ngOnInit(): void { this.cargar(); }

  cargar = async (): Promise<void> => {
    this.cargando = true; this.error = '';
    try { const r = await this.liq.noProcesados(); this.pagos = (r && r.pagos) || []; this.marcadas.clear(); }
    catch (e: any) { this.error = e?.error?.msg || 'No se pudieron leer los pagos no procesados.'; }
    finally { this.cargando = false; }
  };

  get pagadosTrasAnular(): any[] { return this.pagos.filter((p) => p.tipo === 'PAGADO_TRAS_ANULAR'); }
  get noProcesados(): any[] { return this.pagos.filter((p) => p.tipo !== 'PAGADO_TRAS_ANULAR'); }

  /** LOT-16: REGISTRAR el pago que hizo el banco, o DESCARTAR (el banco lo reversó) con motivo. */
  resolverTrasAnular = async (p: any, accion: 'REGISTRAR' | 'DESCARTAR'): Promise<void> => {
    if (accion === 'DESCARTAR' && !this.motivoDescartar.trim()) { this.error = 'Para descartar escribe el motivo.'; return; }
    this.ocupado = true; this.error = ''; this.aviso = '';
    try {
      await this.liq.resolverPagadoTrasAnular(p.code, accion, accion === 'DESCARTAR' ? this.motivoDescartar.trim() : '');
      this.aviso = accion === 'REGISTRAR' ? `${p.code} quedó pagada con la referencia ${p.referencia}; se le dejó su aviso al comercio.` : `${p.code}: el pago después de anular quedó descartado.`;
      this.descartando = null; this.motivoDescartar = '';
      await this.cargar();
    } catch (e: any) { this.error = e?.error?.msg || 'No se pudo resolver.'; }
    finally { this.ocupado = false; }
  };

  marcar = (p: any): void => { this.marcadas.has(p.code) ? this.marcadas.delete(p.code) : this.marcadas.add(p.code); };
  get seleccion(): string[] { return [...this.marcadas]; }
  get monto(): number { return Math.round(this.noProcesados.filter((p) => this.marcadas.has(p.code)).reduce((s, p) => s + Number(p.monto || 0), 0) * 100) / 100; }
  /** NOP-03: si alguna marcada tiene la cuenta sin verificar, no hay propuesta nueva (solo retener). */
  get todasConCuenta(): boolean { return this.noProcesados.filter((p) => this.marcadas.has(p.code)).every((p) => p.cuenta_lista); }

  /** NOP-02: la propuesta nueva va como tanda de reintento del cierre de hoy y sigue las bandas. */
  proponer = async (): Promise<void> => {
    if (!this.marcadas.size || !this.todasConCuenta) return;
    this.ocupado = true; this.error = ''; this.aviso = '';
    try {
      const t = await this.liq.proponerReintento(this.dia, this.seleccion);
      this.aviso = t.state === 'APROBADA'
        ? `Propuesta nueva aprobada (tanda ${t.n}, banda ${t.banda.n}). Arma su lote en «Lotes».`
        : `Propuesta nueva enviada como tanda ${t.n}: espera la aprobación de la banda ${t.banda.n}.`;
      await this.cargar();
    } catch (e: any) { this.error = e?.error?.msg || 'No se pudo enviar la propuesta nueva.'; }
    finally { this.ocupado = false; }
  };

  retener = async (): Promise<void> => {
    if (!this.motivoRetener.trim()) { this.error = 'Para retener escribe el motivo.'; return; }
    this.ocupado = true; this.error = ''; this.aviso = '';
    try {
      await this.liq.retener(this.seleccion, this.motivoRetener.trim());
      this.aviso = `${this.marcadas.size} pago(s) retenido(s): vuelven a aparecer mañana.`;
      this.reteniendo = false; this.motivoRetener = '';
      await this.cargar();
    } catch (e: any) { this.error = e?.error?.msg || 'No se pudo retener.'; }
    finally { this.ocupado = false; }
  };

  verLote = (code: string): void => { this.router.navigate([], { queryParams: { vista: 'lotes', lote: code }, queryParamsHandling: 'merge' }); };
}
